/**
 * Diario Alimenticio — nutritionist view of the patient's food log
 * (expediente tab, adherence loop Fase 1).
 *
 * Unlike the patient view, the professional DOES see the estimated macros
 * from the AI analysis. Fase 2 adds quick emoji reactions from this feed.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid,
  IconButton, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  LocalFireDepartment as KcalIcon,
  RestaurantMenu as RestaurantMenuIcon,
} from '@mui/icons-material';
import foodLogService from '../../services/foodLogService';
import FoodLogPhoto from '../food-log/FoodLogPhoto';

const GREEN = '#006847';
const REACTIONS = ['👍', '🎉', '💪', '❤️', '🤔'];

const MEAL_LABELS = {
  desayuno: '🌅 Desayuno', colacion: '🍎 Colación', comida: '🍲 Comida',
  cena: '🌙 Cena', snack: '🥜 Snack', otro: '🍽️ Otro',
};

const formatWhen = (iso) => {
  const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
  return d.toLocaleString('es-MX', {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit',
  });
};

const MacroChip = ({ label, value, unit }) => (
  value == null ? null : (
    <Chip
      size="small"
      variant="outlined"
      label={`${label}: ${Math.round(value)}${unit}`}
      sx={{ fontSize: 11, borderColor: '#dfe7e2', color: '#4a5d52' }}
    />
  )
);

/** Nutritionist 1-tap reaction bar for a single entry. */
const ReactionBar = ({ entry, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState(entry.reaction_comment || '');
  const [error, setError] = useState('');

  const react = async (emoji) => {
    setBusy(true);
    setError('');
    try {
      const updated = await foodLogService.react(entry.id, emoji, comment.trim());
      onChange(updated);
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo reaccionar.');
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    setError('');
    try {
      const updated = await foodLogService.removeReaction(entry.id);
      onChange(updated);
      setComment('');
      setShowComment(false);
    } catch (e) {
      setError('No se pudo quitar la reacción.');
    } finally {
      setBusy(false);
    }
  };

  const saveComment = async () => {
    if (!entry.reaction) return;
    await react(entry.reaction);
    setShowComment(false);
  };

  return (
    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed #e3e9e5' }}>
      <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
        {REACTIONS.map((emoji) => {
          const active = entry.reaction === emoji;
          return (
            <Tooltip key={emoji} title={active ? 'Reaccionado' : 'Reaccionar'}>
              <span>
                <IconButton
                  size="small"
                  disabled={busy}
                  onClick={() => (active ? clear() : react(emoji))}
                  sx={{
                    fontSize: 20, lineHeight: 1,
                    transform: active ? 'scale(1.25)' : 'none',
                    filter: active ? 'none' : 'grayscale(0.4) opacity(0.75)',
                    bgcolor: active ? '#fff7ec' : 'transparent',
                    transition: 'all .15s',
                    '&:hover': { transform: 'scale(1.2)', filter: 'none' },
                  }}
                >
                  {emoji}
                </IconButton>
              </span>
            </Tooltip>
          );
        })}
        {entry.reaction && (
          <Button
            size="small"
            onClick={() => setShowComment((v) => !v)}
            sx={{ ml: 0.5, color: GREEN, textTransform: 'none', minWidth: 0 }}
          >
            {entry.reaction_comment ? 'Editar nota' : 'Agregar nota'}
          </Button>
        )}
      </Stack>

      {entry.reaction_comment && !showComment && (
        <Typography variant="body2" sx={{ mt: 0.5, color: '#6b4a1f', fontStyle: 'italic' }}>
          “{entry.reaction_comment}”
        </Typography>
      )}

      {showComment && (
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1 }}>
          <TextField
            fullWidth
            size="small"
            multiline
            maxRows={3}
            placeholder="Una palabra de ánimo para tu paciente…"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            inputProps={{ maxLength: 280 }}
          />
          <IconButton size="small" onClick={() => setShowComment(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
          <Button
            size="small"
            variant="contained"
            disabled={busy}
            onClick={saveComment}
            sx={{ bgcolor: GREEN, '&:hover': { bgcolor: '#005035' } }}
          >
            Enviar
          </Button>
        </Stack>
      )}

      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

const DiarioAlimenticioView = ({ paciente }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const patientId = paciente?.id;

  const load = useCallback(async () => {
    if (!patientId) { setLoading(false); return; }
    try {
      setError('');
      const data = await foodLogService.getPatientEntries(patientId, 50);
      setEntries(data);
    } catch (e) {
      if (e?.response?.status === 403) {
        setError('No tienes acceso al diario de este paciente.');
      } else if (e?.response?.status === 404) {
        setError('Paciente no encontrado.');
      } else {
        setError('No se pudo cargar el diario del paciente.');
      }
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const applyEntry = useCallback((updated) => {
    setEntries((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
  }, []);

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress sx={{ color: GREEN }} />
      </Box>
    );
  }

  if (error) return <Alert severity="warning" sx={{ borderRadius: 2 }}>{error}</Alert>;

  if (entries.length === 0) {
    return (
      <Card elevation={0} sx={{ borderRadius: 3, bgcolor: '#f3f7f4', textAlign: 'center', py: 6, px: 3 }}>
        <RestaurantMenuIcon sx={{ fontSize: 48, color: GREEN, mb: 1 }} />
        <Typography variant="h6" sx={{ color: GREEN }}>
          Este paciente aún no registra comidas
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 420, mx: 'auto' }}>
          Cuando registre una comida con foto desde "Mi Diario", aparecerá aquí
          con el análisis nutricional estimado por IA.
        </Typography>
      </Card>
    );
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ color: '#2c3e50', mb: 2 }}>
        🍽️ Diario alimenticio ({entries.length} registros)
      </Typography>
      <Grid container spacing={2}>
        {entries.map((entry) => {
          const tot = entry.ai_analysis?.totales;
          return (
            <Grid item xs={12} sm={6} key={entry.id}>
              <Card elevation={1} sx={{ borderRadius: 3, overflow: 'hidden', height: '100%' }}>
                {entry.has_photo && <FoodLogPhoto entryId={entry.id} height={180} sx={{ borderRadius: 0 }} />}
                <CardContent sx={{ pb: '14px !important' }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Chip
                      size="small"
                      label={MEAL_LABELS[entry.meal_type] || entry.meal_type}
                      sx={{ bgcolor: '#e8f3ee', color: GREEN, fontWeight: 600 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {formatWhen(entry.logged_at)}
                    </Typography>
                  </Stack>

                  {entry.ai_analysis?.platillo?.nombre && (
                    <Typography variant="subtitle2" fontWeight={700}>
                      {entry.ai_analysis.platillo.nombre}
                    </Typography>
                  )}

                  {entry.note && (
                    <Typography variant="body2" sx={{ color: '#555', fontStyle: 'italic', mt: 0.5 }}>
                      “{entry.note}”
                    </Typography>
                  )}

                  {entry.hunger_level && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Hambre al llegar: {'🌶️'.repeat(entry.hunger_level)} ({entry.hunger_level}/5)
                    </Typography>
                  )}

                  {/* Professional-only: estimated macros */}
                  {tot && (
                    <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.6, mt: 1 }}>
                      {tot.calorias != null && (
                        <Chip
                          size="small"
                          icon={<KcalIcon sx={{ fontSize: 14 }} />}
                          label={`${Math.round(tot.calorias)} kcal`}
                          sx={{ bgcolor: '#fff3e6', color: '#b35a00', fontWeight: 700, fontSize: 11 }}
                        />
                      )}
                      <MacroChip label="Prot" value={tot.proteina} unit="g" />
                      <MacroChip label="Carb" value={tot.carbohidratos} unit="g" />
                      <MacroChip label="Gras" value={tot.grasas} unit="g" />
                      <MacroChip label="Fibra" value={tot.fibra} unit="g" />
                    </Stack>
                  )}
                  {entry.ai_status === 'pending' && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Análisis IA en proceso…
                    </Typography>
                  )}
                  {entry.ai_status === 'failed' && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Sin análisis IA para esta foto.
                    </Typography>
                  )}

                  <ReactionBar entry={entry} onChange={applyEntry} />
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default DiarioAlimenticioView;
